// The pure, enumerable directional-sprite asset manifest + the injectable-loader
// bootstrap registry (design "Directional sprite asset registry"). The manifest lists
// every fixed pixel-art texture the Stage-4 render body draws — the 24px `worker`
// fly/walk directional sheets, the per-season ground tiles, the flower variety +
// dead-flower sheets, the static hive tile, and the fixed-frame own-queen anchor sheet
// (D1/D4/D5/D6) — as a plain record so it is unit-testable without a GPU. `AssetRegistry`
// awaits that manifest through an injected `AssetLoader` (the group-1.2 seam), so
// `CanvasRenderer.init()` can block the first sprite draw on a resolved load and a missing
// texture is a load-time failure rather than a silent blank frame. This module holds only
// plain logic (readiness + bundle handoff); the concrete Pixi `Assets.load` loader and the
// draw-time texture use are GPU wiring in `renderer.ts`, exercised on-device only (D-086).

import type { Texture } from 'pixi.js';
import type { AssetLoader } from './asset-loader';

/**
 * Every fixed texture the render body draws, keyed by a semantic name → its
 * `client/public/`-served URL (the `sprites/*` subtree the group-2 sheets ship into).
 * The anchor's hive and own-queen textures are in the manifest so the first anchor draw
 * (group 8) can never reference an unregistered texture (delta requirement). Frozen — the
 * manifest is a constant.
 */
export const ASSET_MANIFEST = Object.freeze({
  // Generic `worker` body-for-all-bees (the wire carries no `type_id`, D1): 12-heading
  // directional sheets, separate flying and walking gaits (D2, UI.md §8).
  workerFly: '/sprites/dir/bee_worker_dir.png',
  workerWalk: '/sprites/dir/bee_worker_walk_dir.png',
  // Fixed-frame own-queen anchor sheet (D4): the whole sheet ships; one frame is drawn.
  ownQueen: '/sprites/dir/bee_queen_walk_dir.png',
  // Per-season ground tiles (D5).
  grassSummer: '/sprites/grass_summer.png',
  grassAutumn: '/sprites/grass_autumn.png',
  grassWinter: '/sprites/grass_winter.png',
  // Flower variety (five live colours, chosen by the D5 patch-id hash) + dead-flower.
  flowerBlue: '/sprites/dir/flower_blue.png',
  flowerPink: '/sprites/dir/flower_pink.png',
  flowerPurple: '/sprites/dir/flower_purple.png',
  flowerRed: '/sprites/dir/flower_red.png',
  flowerWhite: '/sprites/dir/flower_white.png',
  flowerDead: '/sprites/dir/flower_dead.png',
  // Static own-hive tile (D4; the cell-by-cell composition is deferred to D-092).
  hive: '/sprites/dir/hive_tilted.png',
  // The cell-by-cell hive comb (hive-egg-cells): one 32px frame per cell state, in
  // `HIVE_CELL_FRAMES` order — building, intact, damaged, ruin. Built by
  // `tools/blender/build_hive_cell.py`.
  hiveCells: '/sprites/hive_cells.png',
  // World-space ground scatter (field-ground-scatter): two cosmetic variants each of grass
  // tuft and rock, deterministically placed by `ground-scatter.ts` as wayfinding landmarks
  // over the flat season fill (replacing the old tiled ground texture).
  grassTuftA: '/sprites/grass_tuft_a.png',
  grassTuftB: '/sprites/grass_tuft_b.png',
  rockA: '/sprites/rock_a.png',
  rockB: '/sprites/rock_b.png',
});

/** The manifest's static shape (alias → served URL). */
export type AssetManifest = typeof ASSET_MANIFEST;

/** A texture key the render body can request. */
export type AssetKey = keyof AssetManifest;

/** The loaded bundle: one `Texture` per manifest key (production is Pixi's `Assets.load`
 * result, keyed by the manifest alias). */
export type AssetBundle = Readonly<Record<AssetKey, Texture>>;

/**
 * Bootstraps and holds the directional-sprite textures behind an injected `AssetLoader`
 * (design "Directional sprite asset registry"). `load()` awaits the whole `ASSET_MANIFEST`
 * once; a rejection propagates so `CanvasRenderer.init()` surfaces it as an initialization
 * failure (never a swallowed blank render). Until it resolves the registry is `!ready` and
 * `texture()` throws — the gate that keeps the first sprite draw from referencing an
 * unloaded texture. Plain logic (no Pixi runtime): unit-tested against the group-1.2 fake
 * loader in `assets.test.ts`; the GPU composition into `CanvasRenderer` is on-device-only
 * (D-086).
 */
export class AssetRegistry {
  private bundle: AssetBundle | null = null;

  constructor(private readonly loader: AssetLoader<AssetManifest, AssetBundle>) {}

  /** True once `load()` has resolved — the render body draws sprites only when ready. */
  get ready(): boolean {
    return this.bundle !== null;
  }

  /** Awaits the full manifest through the injected loader (call once from `init()`). A
   * rejection is re-thrown so init fails rather than the renderer drawing blank frames. */
  async load(): Promise<void> {
    this.bundle = await this.loader.load(ASSET_MANIFEST);
  }

  /** The loaded texture for `key`. Throws before `load()` resolves, so a premature draw
   * is a loud failure rather than a silent blank frame. */
  texture(key: AssetKey): Texture {
    if (this.bundle === null) {
      throw new Error(
        `asset registry not ready: texture "${key}" requested before load() resolved`,
      );
    }
    return this.bundle[key];
  }
}
